import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResponse } from './store.models';
import { API_BASE } from './ordering-api';
import { environment } from '../../../../environments/environment';

/** The customer's orderYaa coin wallet (mirrors /api/public/coins CoinWalletDto). */
export interface CoinWallet {
  balance: number;
  /** Days completed in the current streak cycle (0 = fresh cycle). */
  streakDay: number;
  streakTargetDays: number;
  collectedToday: boolean;
  /** Free deliveries held outright (earned by completing a streak, or granted by support). */
  freeDeliveryVouchers: number;
  dailyCoins: number;
  orderCoins: number;
  freeDeliveryCoinCost: number;

  /** Programme master switch — false ⇒ hide every rewards surface. */
  enabled: boolean;
  /** Coins may be spent on free delivery. */
  coinRedemptionEnabled: boolean;
  /** Completing a streak earns a free delivery. */
  streakRewardEnabled: boolean;
  /** Coins still needed for the next coin-funded free delivery (0 when already affordable). */
  coinsToNextFreeDelivery: number;
  /** Free deliveries usable right now — vouchers plus what the balance affords. */
  freeDeliveriesAvailable: number;
}

/**
 * The platform's reward rules with no wallet — the only rewards call a signed-out visitor can make
 * (mirrors /api/public/coins/rules CoinRulesDto). Drives the sign-in promo, so the numbers advertised
 * are the operator's live ones rather than copy that goes stale when they edit the ERP console.
 */
export interface CoinRules {
  enabled: boolean;
  /** Free deliveries a brand-new account is given (0 ⇒ no welcome gift to advertise). */
  signupFreeDeliveries: number;
  orderCoins: number;
  freeDeliveryCoinCost: number;
  streakTargetDays: number;
  coinRedemptionEnabled: boolean;
  streakRewardEnabled: boolean;
}

/** One movement in the customer's coin ledger. */
export interface CoinTransaction {
  id: string;
  /** 0 check-in, 1 order earn, 2 coins→delivery, 3 streak erased, 4 streak reward, 5 reward used,
   *  6 order-cancelled reversal, 7 platform grant. */
  type: number;
  amount: number;
  balanceAfter: number;
  orderId?: string | null;
  orderNumber?: string | null;
  note?: string | null;
  createdAt: string;
}

/** Paged envelope used by the API's PaginatedResponse<T>. */
export interface PagedResponse<T> {
  success: boolean;
  message?: string;
  data: T[];
  pagination?: { totalCount: number; pageNumber: number; pageSize: number; totalPages: number };
}

/**
 * orderYaa coin rewards. Every number (coins per order, the price of a free delivery, streak length)
 * is set per platform in the ERP console and travels on the wallet — never hard-code them here.
 * Two paths to a free delivery: earn coins by ordering, or complete the daily check-in streak.
 * Customer JWT attached by customerAuthInterceptor.
 */
@Injectable({ providedIn: 'root' })
export class CoinsService {
  private readonly http = inject(HttpClient);
  private readonly base = `${API_BASE}/api/public/coins`;

  get(): Observable<ApiResponse<CoinWallet>> {
    return this.http.get<ApiResponse<CoinWallet>>(this.base);
  }

  collect(): Observable<ApiResponse<CoinWallet>> {
    return this.http.post<ApiResponse<CoinWallet>>(`${this.base}/collect`, {});
  }

  /** Reward rules for a SIGNED-OUT visitor (no wallet) — what the sign-in promo advertises. */
  rules(): Observable<ApiResponse<CoinRules>> {
    const platform = environment.platformCompanyId;
    const q = platform ? `?platformCompanyId=${encodeURIComponent(platform)}` : '';
    return this.http.get<ApiResponse<CoinRules>>(`${this.base}/rules${q}`);
  }

  /** The coin ledger, newest first — what the rewards page shows under "History". */
  history(pageNumber = 1, pageSize = 25): Observable<PagedResponse<CoinTransaction>> {
    return this.http.get<PagedResponse<CoinTransaction>>(
      `${this.base}/history?pageNumber=${pageNumber}&pageSize=${pageSize}`);
  }
}
